import React, { useState, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Label } from 'reactstrap';
import { ImageIcon } from '@saleshandy/design-system';
import RadioButtonGroup from '../../../../../../shared/design-system/components/radio-button-group';
import Input from '../../../../../../shared/design-system/components/input/input';
import { Images } from '../../../../../../shared/app-constants';
import { accessibleOnClick } from '../../../../../../shared/utils/accessible-on-click';
import toaster, { Theme } from '../../../../../../shared/toaster';
import { getIsRequestPending } from '../../../../../../shared/utils';
import {
  executeOnRequestStatus,
  executeOnErrorWithErrorCheck,
} from '../../../../../../shared/utils/execute-on-request-status';
import { AiColEnrichmentModalSteps } from '../../enums';
import { AiColSaveAsTemplateModalProps } from './types';
import { convertHtmlToText } from '../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-parsed-content-for-text-only-email';
import { stripChipTagsFromHtml } from '../../../../../../shared/editor/helpers';
import { br2nl } from '../../../../../sequence/shared/modals/email-modal/components/sidebar/tabs/utils/get-merge-tag-mapping-with-values';

const AiColSaveAsTemplateModal: React.FC<AiColSaveAsTemplateModalProps> = ({
  show,
  onClose,
  templateData,
  templateId,
  currentStep,
  createAiColumnTemplateRequests,
  updateAiColumnTemplateRequests,
  getTemplateByIdRequests,
  resetCreateAiColumnTemplateRequests,
  resetUpdateAiColumnTemplateRequests,
  createAiColumnTemplateRequestState,
  updateAiColumnTemplateRequestState,
  selectedAiTemplateId,
}) => {
  const options = [
    {
      label: 'Save as new template',
      value: 'save',
      disabled: false,
    },
    {
      label: 'Update existing template',
      value: 'update',
      disabled:
        currentStep !== AiColEnrichmentModalSteps.TEMPLATE_DETAIL ||
        !templateId,
    },
  ];

  const [selectedOption, setSelectedOption] = useState('save');
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const [touchedFields, setTouchedFields] = useState({
    templateName: false,
    templateDescription: false,
  });

  // Handle radio button changes to prefill/clear inputs
  useEffect(() => {
    if (selectedOption === 'update') {
      // Prefill inputs when UPDATE is selected
      setTemplateName(templateData?.title || '');
      setTemplateDescription(templateData?.description || '');
    } else {
      // Clear inputs when SAVE is selected
      setTemplateName('');
      setTemplateDescription('');
    }
    // Reset touched fields when switching modes
    setTouchedFields({
      templateName: false,
      templateDescription: false,
    });
    // Clear validation errors when switching modes
    setValidationErrors([]);
  }, [selectedOption, templateData]);

  // Initialize inputs based on initial selectedOption
  useEffect(() => {
    if (selectedOption === 'update') {
      setTemplateName(templateData?.title || '');
      setTemplateDescription(templateData?.description || '');
    }
  }, []);

  const isCreatePending = getIsRequestPending(
    createAiColumnTemplateRequestState?.status,
  );
  const isUpdatePending = getIsRequestPending(
    updateAiColumnTemplateRequestState?.status,
  );
  const isRequestPending = isCreatePending || isUpdatePending;

  // Handle request status changes using executeOnRequestStatus
  useEffect(() => {
    if (!createAiColumnTemplateRequestState) return;

    executeOnRequestStatus({
      status: createAiColumnTemplateRequestState.status,
      onSuccess: () => {
        if (createAiColumnTemplateRequestState.message) {
          toaster.success(createAiColumnTemplateRequestState.message, {
            theme: Theme.New,
          });
        } else {
          toaster.success('Template Saved Successfully', { theme: Theme.New });
        }
        resetCreateAiColumnTemplateRequests?.();
        onClose();
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: createAiColumnTemplateRequestState.error,
          onError: () => {
            toaster.error(
              createAiColumnTemplateRequestState.error?.message ||
                'Failed to create template',
              {
                theme: Theme.New,
              },
            );
          },
        });
      },
    });
  }, [createAiColumnTemplateRequestState?.status]);

  useEffect(() => {
    if (!updateAiColumnTemplateRequestState) return;

    executeOnRequestStatus({
      status: updateAiColumnTemplateRequestState.status,
      onSuccess: () => {
        if (updateAiColumnTemplateRequestState.message) {
          toaster.success(updateAiColumnTemplateRequestState.message, {
            theme: Theme.New,
          });
        } else {
          toaster.success('Template Updated Successfully', {
            theme: Theme.New,
          });
        }
        if (selectedAiTemplateId) {
          getTemplateByIdRequests?.({ templateId: selectedAiTemplateId });
        }
        resetUpdateAiColumnTemplateRequests?.();
        onClose();
      },
      onFailed: () => {
        executeOnErrorWithErrorCheck({
          error: updateAiColumnTemplateRequestState.error,
          onError: () => {
            toaster.error(
              updateAiColumnTemplateRequestState.error?.message ||
                'Failed to update template',
              {
                theme: Theme.New,
              },
            );
          },
        });
        resetUpdateAiColumnTemplateRequests?.();
      },
    });
  }, [updateAiColumnTemplateRequestState?.status]);

  const validateForm = (): boolean => {
    const errors: string[] = [];

    // Title validation: REQUIRED for both save and update
    if (!templateName.trim()) {
      errors.push('Template name is required');
    } else if (templateName.trim().length > 100) {
      errors.push('Template name must be 100 characters or less');
    }

    // Description validation: OPTIONAL, 0-250 chars
    if (templateDescription.trim().length > 250) {
      errors.push('Description must be 250 characters or less');
    }

    // Merge tags validation: REQUIRED, at least 1 tag
    if (!templateData.mergeTags || templateData.mergeTags.length === 0) {
      errors.push('At least one merge tag is required');
    }

    // AI Model validation: REQUIRED, positive number
    if (
      !templateData.shSupportedAiModel ||
      templateData.shSupportedAiModel <= 0
    ) {
      errors.push('AI model selection is required');
    }

    // Instructions validation: REQUIRED, 20-5000 chars
    const instructionsLength = br2nl(convertHtmlToText(stripChipTagsFromHtml(templateData?.instructions || '')))
                    ?.replace(/<[^>]*>/g, '')
                    ?.trim()?.length;
    if (
      !templateData?.instructions ||
      instructionsLength < 20
    ) {
      errors.push('Instructions must be at least 20 characters long');
    } else if (instructionsLength > 5000) {
      errors.push('Instructions must be 5000 characters or less');
    }

    // Output format validation: REQUIRED
    if (!templateData.selectedOutputFormat) {
      errors.push('Output format selection is required');
    }

    // Custom format validation: conditional based on selectedOutputFormat
    if (templateData.selectedOutputFormat === 'custom') {
      if (!templateData.customOutputFormatPrompt) {
        errors.push(
          'Custom output format prompt is required when format is custom',
        );
      }
      if (!templateData.customOutputFormatColumnType) {
        errors.push(
          'Custom output format column type is required when format is custom',
        );
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleSaveTemplate = async () => {
    if (!validateForm()) {
      return;
    }

    if (selectedOption === 'update') {
      // For updates, title is now required
      const updatePayload: any = {
        title: templateName.trim(),
        description: templateDescription.trim() || undefined,
        mergeTags: templateData.mergeTags,
        isWebSearchEnabled: templateData.isWebSearchEnabled,
        shSupportedAiModel: templateData.shSupportedAiModel,
        instructions: br2nl(convertHtmlToText(
          stripChipTagsFromHtml(templateData?.instructions || ''),
        ))?.replace(/<[^>]*>/g, '')?.trim() || '',
        selectedOutputFormat: templateData.selectedOutputFormat,
        customOutputFormatPrompt:
          templateData.selectedOutputFormat === 'custom'
            ? templateData.customOutputFormatPrompt
            : undefined,
        customOutputFormatColumnType:
          templateData.selectedOutputFormat === 'custom'
            ? templateData.customOutputFormatColumnType
            : undefined,
        isAutoRunEnabled: templateData.isAutoRunEnabled,
        ...(templateData.autoRunConditions !== null ? {
          autoRunConditions: templateData.autoRunConditions,
        } : {}),
      };

      if (templateId) {
        updateAiColumnTemplateRequests?.({
          templateId,
          payload: updatePayload,
        });
      }
    } else {
      // For new templates, include all fields including title
      const createPayload = {
        title: templateName.trim(),
        description: templateDescription.trim() || undefined,
        mergeTags: templateData.mergeTags,
        isWebSearchEnabled: templateData.isWebSearchEnabled,
        shSupportedAiModel: templateData.shSupportedAiModel,
        instructions: br2nl(convertHtmlToText(
          stripChipTagsFromHtml(templateData?.instructions || ''),
        ))?.replace(/<[^>]*>/g, '')?.trim() || '',
        selectedOutputFormat: templateData.selectedOutputFormat,
        customOutputFormatPrompt:
          templateData.selectedOutputFormat === 'custom'
            ? templateData.customOutputFormatPrompt
            : undefined,
        customOutputFormatColumnType:
          templateData.selectedOutputFormat === 'custom'
            ? templateData.customOutputFormatColumnType
            : undefined,

        isAutoRunEnabled: templateData.isAutoRunEnabled,
        ...(templateData.autoRunConditions !== null ? {
          autoRunConditions: templateData.autoRunConditions,
        } : {}),
      };
      createAiColumnTemplateRequests?.(createPayload);
    }
  };

  const getButtonText = () => {
    if (selectedOption === 'save') {
      return 'Save Template';
    }
    return 'Update Template';
  };

  const getLoadingText = () => {
    if (isRequestPending) {
      return selectedOption === 'update' ? 'Updating...' : 'Saving...';
    }
    return getButtonText();
  };

  const onTemplateNameChange = (value: string) => {
    if (!touchedFields.templateName) {
      setTouchedFields((prev) => ({ ...prev, templateName: true }));
    }
    setTemplateName(value);
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const onTemplateDescriptionChange = (value: string) => {
    if (!touchedFields.templateDescription) {
      setTouchedFields((prev) => ({ ...prev, templateDescription: true }));
    }
    setTemplateDescription(value);
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  // Run validation after changes, but only once a field is touched
  useEffect(() => {
    if (touchedFields.templateName || touchedFields.templateDescription) {
      validateForm();
    }
  }, [templateName, templateDescription, templateData, touchedFields]);

  const isDisabled =
    isRequestPending || !templateName.trim() || validationErrors.length > 0;

  return (
    <Modal
      show={show}
      onHide={onClose}
      backdrop="static"
      centered
      className="save-as-template-modal"
    >
      <>
        <div className="save-as-template-modal-body">
          <div className="save-as-template-header">
            <ImageIcon src={Images.Device} alt="device-icon" />
            <span>Save Template</span>
          </div>
          <div className="save-as-template-content">
            <RadioButtonGroup
              options={options}
              value={selectedOption}
              onChange={(value) => setSelectedOption(value)}
              name="save-as-template"
            />
            <div className="template-details">
              <div className="template-name-input">
                <Label className="template-name-label">Template Name*</Label>
                <Input
                  placeholder="e.g B2B data"
                  className={`template-name-input-placeholder ${
                    validationErrors.some((error) =>
                      error.toLowerCase().includes('template name'),
                    )
                      ? 'is-invalid'
                      : ''
                  }`}
                  value={templateName}
                  onChange={onTemplateNameChange}
                />
                {/* Show template name validation errors inline */}
                {validationErrors
                  .filter(
                    (error) =>
                      error.includes('Template name') || error.includes('name'),
                  )
                  .map((error) => (
                    <div key={error} className="validation-errors">
                      <div className="error-message">{error}</div>
                    </div>
                  ))}
              </div>
              <div className="template-description-input">
                <Label className="template-description-label">
                  Template Description
                </Label>
                <Input
                  placeholder="e.g Provide prospect's service URL"
                  className={`template-description-input-placeholder ${
                    validationErrors.some((error) =>
                      error.toLowerCase().includes('description'),
                    )
                      ? 'is-invalid'
                      : ''
                  }`}
                  value={templateDescription}
                  onChange={onTemplateDescriptionChange}
                />
                {/* Show template description validation errors inline */}
                {validationErrors
                  .filter(
                    (error) =>
                      error.includes('Description') ||
                      error.includes('description'),
                  )
                  .map((error) => (
                    <div key={error} className="validation-errors">
                      <div className="error-message">{error}</div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Display other validation errors (merge tags, AI model, instructions, output format) */}
            {validationErrors
              .filter(
                (error) =>
                  !error.includes('Template name') &&
                  !error.includes('name') &&
                  !error.includes('Description') &&
                  !error.includes('description'),
              )
              .map((error) => (
                <div key={error} className="validation-errors">
                  <div className="error-message">{error}</div>
                </div>
              ))}
          </div>
        </div>
        <div className="divider-line" />
        <div className="save-as-template-footer">
          <div className="cancel-button" {...accessibleOnClick(onClose)}>
            Cancel
          </div>
          <div className="save-template-btn">
            <Button
              onClick={handleSaveTemplate}
              disabled={isDisabled}
              className={`${isDisabled ? 'disabled' : ''}`}
            >
              {getLoadingText()}
            </Button>
          </div>
        </div>
      </>
    </Modal>
  );
};

export default AiColSaveAsTemplateModal;
